import queryString from 'query-string';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import queryStringOptions from '@/constants/queryStringOptions';

/**
 * Custom hook for reading and writing URL search parameters using query-string library.
 *
 * @template T - The type of the query parameters object
 * @param options - Configuration options
 * @param options.replace - If true, replaces the current history entry instead of pushing a new one (default: true)
 * @param options.skipEmptyString - If true, empty strings are excluded from the query string (default: true)
 *
 * @returns An object containing:
 *   - params: The current parsed query parameters
 *   - setParams: Function to update the query parameters
 *   - clearParams: Function to clear all query parameters
 *   - hasQueryParams: Boolean indicating if any query parameters exist
 *
 * @example
 * ```tsx
 * const { params, setParams, hasQueryParams } = useQueryString<{ search: string; page: string }>();
 *
 * // Check if query params exist
 * if (hasQueryParams) {
 *   console.log('Using query params:', params);
 * } else {
 *   console.log('Using defaults');
 * }
 *
 * // Update params
 * setParams({ search: "new value", page: "2" });
 * ```
 */
const useQueryString = <T extends Record<string, unknown>>({
  replace = true,
  skipEmptyString = true
}: {
  replace?: boolean;
  skipEmptyString?: boolean;
} = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse current query parameters
  const params = useMemo(() => {
    return queryString.parse(location.search, queryStringOptions) as Partial<T>;
  }, [location.search]);

  // Check if query parameters exist
  const hasQueryParams = useMemo(() => {
    return Object.keys(params).length > 0;
  }, [params]);

  // Update query parameters
  const setParams = useCallback(
    (newParams: Partial<T>) => {
      // Parse current params from location to avoid stale closure
      const currentParams = queryString.parse(location.search, queryStringOptions) as Partial<T>;
      const mergedParams = { ...currentParams, ...newParams };
      const queryStr = queryString.stringify(mergedParams, {
        ...queryStringOptions,
        skipEmptyString
      });
      navigate(
        {
          pathname: location.pathname,
          search: queryStr ? `?${queryStr}` : '',
          hash: location.hash
        },
        { replace, state: location.state }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, location.pathname, location.search, replace, skipEmptyString]
  );

  // Clear all query parameters
  const clearParams = useCallback(() => {
    navigate(
      {
        pathname: location.pathname,
        hash: location.hash
      },
      { replace, state: location.state }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.pathname, replace]);

  return {
    params,
    setParams,
    clearParams,
    hasQueryParams
  };
};

export default useQueryString;
